import { plainToClass } from "class-transformer";
import EshopRequestBuilder from "data/eshopRequestBuilder";
import formatISO from "date-fns/formatISO";
import ChargeValidity from "models/chargeValidity";
import { ContactEditationTriggerer } from "models/contact";
import EshopError from "models/eshopError";
import AuthorizationService from "services/authorizationService";
import EventBus from "services/eventBus";
import VehicleChangeDuringValidityRequest from "../models/vehicleChangeDuringValidityRequest";
import RepositoryBase from "./repositoryBase";
import ContactInfoChangeRequest from "entities/contactInfoChangeRequest";
import ChargePriceListsResponse from "entities/chargePriceListsResponse";
import ChargeDetailResponse from "entities/chargeDetailResponse";
import ChargeValidityRequest from "entities/chargeValidityRequest";

const GET_CHARGE_VALIDITY_REQUEST_FAILURE = "GET_CHARGE_VALIDITY_REQUEST_FAILURE";
const GET_CHARGE_REQUEST_FAILURE = "GET_CHARGE_REQUEST_FAILURE";
export const CHARGE_NOT_FOUND = "CHARGE_NOT_FOUND";
const REQUEST_MODEL_VALIDATION_FAILED = "REQUEST_MODEL_VALIDATION_FAILED";

export default class ChargeRepository extends RepositoryBase<EshopRequestBuilder> {
  constructor(
    protected apiFactory: ReturnType<typeof EshopRequestBuilder.Factory>,
    protected authorizationService: AuthorizationService,
    protected eventBus: EventBus
  ) {
    super(() => apiFactory(), authorizationService, eventBus);
  }

  private handleEmptyResponse = async (response: Response) => {
    if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

    return undefined;
  };

  chargePriceList(includeInvalid = false) {
    return this.callApi(api =>
      api
        .apiVersion(3.0)
        .all("charges")
        .all("price_lists")
        .clearGet({ include_invalid: includeInvalid, version: 3 })
        .then(async response => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          return plainToClass(ChargePriceListsResponse, (await response.json()) as ChargePriceListsResponse);
        })
        .catch(() => {
          throw new EshopError(REQUEST_MODEL_VALIDATION_FAILED);
        })
    );
  }

  chargeValidity(chargeTypeId: string, validSince: Date) {
    const queryValidSince = new Date(validSince);

    return this.callApi(api =>
      api
        .all("charges")
        .one("charge_types", chargeTypeId)
        .all("charge_validity")
        .clearGet({
          // eslint-disable-next-line
          valid_since: formatISO(queryValidSince),
        })
        .then(async response => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          return plainToClass(ChargeValidity, (await response.json()) as ChargeValidity);
        })
        .catch(() => {
          throw new EshopError(GET_CHARGE_VALIDITY_REQUEST_FAILURE);
        })
    );
  }

  getCharge(countryId: string, licensePlate: string, authCode: string, recaptchaToken: string) {
    return this.callApi(api =>
      api
        .apiVersion(3.0)
        .one("charges", `${countryId}/${licensePlate}`)
        .clearGet(undefined, authCode, undefined, undefined, recaptchaToken)
        .then(async response => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          return plainToClass(ChargeDetailResponse, (await response.json()) as ChargeDetailResponse);
        })
        .catch(() => {
          throw new EshopError(GET_CHARGE_REQUEST_FAILURE);
        })
    );
  }

  editChargeValidity(
    countryId: string,
    licensePlate: string,
    emailAddress: string | undefined,
    authCode: string,
    connectionId: string,
    request: ChargeValidityRequest,
    recaptchaToken: string
  ): Promise<EshopError | undefined | void> {
    return this.callApi(
      api =>
        api
          .apiVersion(3)
          .one("charges", `${countryId}/${licensePlate}`)
          .all("validity")
          .clearPut({ emailAddress: emailAddress, validity: request }, authCode, connectionId, recaptchaToken)
          .then(async response => await this.handleEmptyResponse(response)),
      false
    );
  }

  editVehicle(
    countryId: string,
    licensePlate: string,
    emailAddress: string | undefined,
    authCode: string,
    connectionId: string,
    newCountryId: string,
    newLicensePlate?: string,
    recaptchaToken?: string
  ): Promise<EshopError | undefined | void> {
    const request = { countryId: newCountryId, licensePlate: newLicensePlate, emailAddress: emailAddress };
    return this.callApi(
      api =>
        api
          .apiVersion(3)
          .one("charges", `${countryId}/${licensePlate}`)
          .all("vehicle")
          .clearPut(request, authCode, connectionId, recaptchaToken)
          .then(async response => await this.handleEmptyResponse(response)),
      false
    );
  }

  editActiveChargeVehicle(
    countryId: string,
    licensePlate: string,
    authCode: string,
    connectionId: string,
    request: VehicleChangeDuringValidityRequest,
    recaptchaToken: string
  ) {
    return this.callApi(
      api =>
        api
          .apiVersion(3)
          .one("active_charges", `${countryId}/${licensePlate}`)
          .all("vehicle")
          .clearPut(request, authCode, connectionId, recaptchaToken)
          .then(async response => await this.handleEmptyResponse(response)),
      false
    );
  }

  editContact(
    request: ContactInfoChangeRequest,
    countryId: string,
    licensePlate: string,
    authCode: string,
    connectionId: string,
    triggerer: ContactEditationTriggerer,
    recaptchaToken: string
  ): Promise<EshopError | undefined | void> {
    return this.callApi(
      api =>
        api
          .apiVersion(3)
          .one("charges", `${countryId}/${licensePlate}`)
          .all("contact")
          .clearPut(request, authCode, connectionId, recaptchaToken)
          .then(async response => await this.handleEmptyResponse(response)),
      false
    );
  }

  sendChargePaymentConfirmationEmail(
    countryId: string,
    licensePlate: string,
    emailAddress: string | undefined,
    authCode: string,
    recaptchaToken: string
  ) {
    return this.callApi(
      api =>
        api
          .apiVersion(3.0)
          .one("charges", `${countryId}/${licensePlate}`)
          .all("payment_confirmation_email")
          .clearPost({ emailAddress: emailAddress }, authCode, undefined, recaptchaToken)
          .then(async response => await this.handleEmptyResponse(response)),
      false
    );
  }

  downloadPaymentConfirmation(countryId: string, licensePlate: string, authCode: string, recaptchaToken: string) {
    return this.callApi(
      api =>
        api
          .apiVersion(3.0)
          .one("charges", `${countryId}/${licensePlate}`)
          .all("receipt")
          .clearGet(undefined, authCode, undefined, undefined, recaptchaToken)
          .then(async response => {
            if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

            return response.url;
          }),
      false
    );
  }
}
